import { useEffect, type FormEvent } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const services = [
  {
    title: 'Guest Experience',
    body: 'Fast responses, clear communication, and a stay experience designed to create repeat guests.',
  },
  {
    title: 'Property Presentation',
    body: 'Professional photography, video, drone imagery, Matterport, and listing presentation that shows the property well.',
  },
  {
    title: 'Housekeeping',
    body: 'Multi-step cleaning and inspection standards, with enhanced linen options available when appropriate.',
  },
  {
    title: 'Maintenance',
    body: 'Local eyes on the property, proactive checks, trusted vendors, and direct communication when something needs attention.',
  },
  {
    title: 'Pricing',
    body: 'Modern booking tools and active rate monitoring to help the property compete in a changing market.',
  },
  {
    title: 'Owner Portal',
    body: '24/7 online or app access to rental calendars, booking details, rental income, and easy-to-read owner statements.',
  },
];

const values = ['Boutique portfolio', 'Local ownership', 'Personal communication', 'Measured performance'];

export default function OwnersPage() {
  useEffect(() => {
    document.title = 'Join Our Rental Program – Inspire Vacation Properties';
  }, []);

  const handleEvaluationRequest = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const value = (field: string) => String(formData.get(field) ?? '').trim();
    const subject = encodeURIComponent('Free property evaluation request');
    const body = encodeURIComponent(
      [
        `Name: ${value('name')}`,
        `Email: ${value('email')}`,
        `Phone: ${value('phone') || 'Not provided'}`,
        `Property location: ${value('property-location')}`,
        `Property type: ${value('property-type') || 'Not provided'}`,
        `Current rental status: ${value('rental-status') || 'Not provided'}`,
        '',
        'Property details:',
        value('property-notes') || 'Not provided',
      ].join('\n'),
    );

    window.location.href = `mailto:AP@InspireVP.com?subject=${subject}&body=${body}`;
  };

  return (
    <main className="bg-background-50 text-foreground-900 font-body">
      <Header />

      <section className="relative min-h-[82svh] overflow-hidden">
        <img
          src="/images/owners-hero1.jpg"
          alt="Aerial view of Panama City Beach and the Gulf"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/25 to-foreground-950/75" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 min-h-[82svh] flex items-end pb-16 md:pb-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-background-100/85 mb-6 md:mb-8">
              <span className="w-6 h-px bg-accent-500" />
              Owner Partnerships
            </div>
            <h1 className="font-heading text-background-50 text-[46px] leading-[1.03] md:text-[84px] md:leading-[1.0] tracking-[-0.01em]">
              Unlock the true potential of your vacation property.
            </h1>
            <p className="mt-5 md:mt-6 max-w-2xl font-heading text-background-100 text-[24px] md:text-[36px] leading-[1.12] italic font-light">
              Boutique management. Local care. A more personal ownership experience.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#property-evaluation"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap shadow-[0_16px_34px_rgba(0,0,0,0.24)] hover:bg-accent-600 transition-colors"
              >
                Get your free property evaluation
                <i className="ri-arrow-right-up-line" />
              </a>
              <a
                href="tel:8508192764"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg border border-background-50/55 bg-background-50 text-foreground-950 text-sm cursor-pointer whitespace-nowrap shadow-[0_16px_34px_rgba(0,0,0,0.24)] hover:bg-[#EFE4D6] transition-colors"
              >
                850-819-2764
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
                <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
                The difference
              </div>
              <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
                Small by design.
                <span className="block italic font-light text-foreground-600">Hands-on by nature.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-foreground-700 text-lg md:text-xl leading-relaxed font-light">
                Inspire Vacation Properties is built for owners who want more than a large-company
                management model. The portfolio is intentionally selective, the communication is
                personal, and each property is expected to meet a high standard before it joins the
                collection.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {values.map((value) => (
                  <div
                    key={value}
                    className="border border-background-200 bg-background-100/60 rounded-lg px-4 py-4 text-sm uppercase tracking-[0.16em] text-foreground-700"
                  >
                    {value}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100/50 py-20 md:py-32 border-y border-background-200/70">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="mb-12 md:mb-16 max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
              <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
              What owners can expect
            </div>
            <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              The details that make
              <span className="block italic font-light text-foreground-600">the stay work.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service) => (
              <article
                key={service.title}
                className="bg-background-50/80 border border-background-200/80 rounded-lg p-7 md:p-8"
              >
                <h3 className="font-heading text-2xl text-foreground-950 leading-tight">
                  {service.title}
                </h3>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">{service.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        id="property-evaluation"
        className="scroll-mt-24 bg-background-50 py-20 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <img
                src="/images/owners-evaluation.webp"
                alt="Inspire-managed vacation property with a golf cart and bicycles"
                className="w-full aspect-[4/5] object-cover object-center rounded-2xl"
              />
            </div>
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
                <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
                Free consultation
              </div>
              <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
                Let&rsquo;s look at
                <span className="block italic font-light text-foreground-600">what your property could become.</span>
              </h2>
              <p className="mt-6 text-foreground-700 text-base md:text-lg leading-relaxed font-light max-w-xl">
                Start with a conversation, a property review, and a rental-income projection. No big
                management-company pitch, just a clear look at fit, standards, and potential.
              </p>
              <p className="mt-6 text-sm text-foreground-500">
                Prefer direct contact? <a href="mailto:AP@InspireVP.com" className="underline underline-offset-4 hover:text-foreground-950">Email us</a> or call <a href="tel:8508192764" className="underline underline-offset-4 hover:text-foreground-950">850-819-2764</a>.
              </p>

              <form
                onSubmit={handleEvaluationRequest}
                className="mt-8 bg-background-100/70 border border-background-200/80 rounded-lg p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Your name
                    </span>
                    <input
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      placeholder="Jane Smith"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Email address
                    </span>
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      placeholder="jane@example.com"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Phone number
                    </span>
                    <input
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      placeholder="(850) 555-0123"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Property location
                    </span>
                    <input
                      name="property-location"
                      type="text"
                      autoComplete="street-address"
                      required
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      placeholder="30A, Panama City Beach..."
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Property type
                    </span>
                    <select
                      name="property-type"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      defaultValue=""
                    >
                      <option value="">Select...</option>
                      <option>Single-family property</option>
                      <option>Condo</option>
                      <option>Townhome</option>
                      <option>Not sure yet</option>
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                      Current rental status
                    </span>
                    <select
                      name="rental-status"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20"
                      defaultValue=""
                    >
                      <option value="">Select...</option>
                      <option>Currently self-managed</option>
                      <option>Currently with another manager</option>
                      <option>New to vacation rentals</option>
                      <option>Still considering my options</option>
                    </select>
                  </label>
                </div>

                <label className="block mt-5">
                  <span className="block text-[11px] uppercase tracking-[0.2em] text-foreground-500 mb-2">
                    Tell us about the property
                  </span>
                  <textarea
                    name="property-notes"
                    rows={5}
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none transition-colors focus:border-foreground-700 focus:ring-2 focus:ring-accent-500/20 resize-y"
                    placeholder="Bedrooms, location, current rental status, goals, questions..."
                  />
                </label>

                <button
                  type="submit"
                  className="mt-6 w-full bg-foreground-950 text-background-50 px-6 py-4 rounded-lg text-sm cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  Request my free property evaluation
                </button>
                <p className="mt-4 text-center text-xs text-foreground-400">
                  Your email app will open with your details ready to send. The Inspire team will
                  follow up personally.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
